import React, { useState } from 'react';
import { List, useTable, ShowButton } from '@refinedev/antd';
import { Link } from 'react-router-dom';
import { Table, Space, Tag, Select } from 'antd';

// NOTE: this local shape mirrors @wellverse/shared-ts's AdminReferralDto,
// matching how the subscriptions resource declares its own row type.
interface ReferralParty {
  id: string;
  displayName: string;
  referralCode?: string;
}

interface ReferralReward {
  id: string;
  beneficiaryId: string;
  beneficiaryName: string;
  side: 'REFERRER' | 'REFEREE';
  days: number;
  status: string;
  creditedAt?: string | null;
  createdAt: string;
}

interface ReferralDto {
  id: string;
  code: string;
  createdAt: string;
  referrer: ReferralParty;
  referee: ReferralParty;
  referrerReward?: ReferralReward | null;
  refereeReward?: ReferralReward | null;
}

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

const statusColors: Record<string, string> = {
  CREDITED: '#3ecf8e',
  PENDING: '#f59e0b',
  REVOKED: '#ef4444',
};

const rewardTag = (reward?: ReferralReward | null) =>
  reward ? (
    <Tag style={tagStyle(statusColors[reward.status] || '#898989')}>{reward.status}</Tag>
  ) : (
    <span>—</span>
  );

const partyLink = (party: ReferralParty) => (
  <Link to={`/users/show/${party.id}`}>{party.displayName || party.id}</Link>
);

export const ReferralList: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  const { tableProps } = useTable<ReferralDto>({
    resource: 'referrals',
    filters: {
      permanent: statusFilter
        ? [{ field: 'status', operator: 'eq' as const, value: statusFilter }]
        : [],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  return (
    <List resource="referrals">
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Select
          placeholder="Reward status"
          allowClear
          style={{ width: 180 }}
          onChange={(value) => setStatusFilter(value)}
          options={[
            { label: 'Pending', value: 'PENDING' },
            { label: 'Credited', value: 'CREDITED' },
            { label: 'Revoked', value: 'REVOKED' },
          ]}
        />
      </div>
      <Table {...tableProps} rowKey="id" scroll={{ x: 1200 }}>
        <Table.Column
          title="Referrer"
          render={(_: unknown, record: ReferralDto) => (
            <Space direction="vertical" size={0}>
              {partyLink(record.referrer)}
              {record.referrer.referralCode && (
                <span style={{ fontSize: 12, opacity: 0.65 }}>{record.referrer.referralCode}</span>
              )}
            </Space>
          )}
        />
        <Table.Column
          title="Referee"
          render={(_: unknown, record: ReferralDto) => partyLink(record.referee)}
        />
        <Table.Column dataIndex="code" title="Code Used" sorter />
        <Table.Column
          dataIndex="createdAt"
          title="Created"
          sorter
          defaultSortOrder="descend"
          render={(v: string) => new Date(v).toLocaleDateString()}
        />
        <Table.Column
          title="Referrer Reward"
          render={(_: unknown, record: ReferralDto) => rewardTag(record.referrerReward)}
        />
        <Table.Column
          title="Referee Reward"
          render={(_: unknown, record: ReferralDto) => rewardTag(record.refereeReward)}
        />
        <Table.Column
          title="Free Days"
          render={(_: unknown, record: ReferralDto) => {
            // The referee is always credited; the referrer is not once they
            // pass `referral_max_rewarded_per_referrer`, so read the referee first.
            const days = record.refereeReward?.days ?? record.referrerReward?.days;
            return days !== undefined ? `${days} / side` : '—';
          }}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_: unknown, record: ReferralDto) => (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} resource="referrals" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
